import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Bookmark, Loader2, X } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import VariableDefinitionEditor from './VariableDefinitionEditor';
import { hasVariables } from './variableEngine';

export default function SaveFUQDialog({ open, onOpenChange, sqlQuery, sourceTab }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [variableDefs, setVariableDefs] = useState([]);

  const queryHasVars = hasVariables(sqlQuery);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !tags.includes(t)) {
      setTags([...tags, t]);
    }
    setTagInput('');
  };

  const removeTag = (tag) => setTags(tags.filter(t => t !== tag));

  const handleSave = async () => {
    if (!name.trim() || !sqlQuery) return;
    setSaving(true);
    setSaveError(null);
    try {
      const user = await base44.auth.me();
      await base44.entities.FrequentlyUsedQuery.create({
        name: name.trim(),
        query: sqlQuery,
        description: description.trim() || undefined,
        source_tab: sourceTab || 'ask_data',
        tags: tags.length > 0 ? tags : undefined,
        usage_count: 0,
        created_by: user?.email,
        variable_definitions: variableDefs.length > 0 ? JSON.stringify(variableDefs) : undefined
      });
      setSaving(false);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        setName('');
        setDescription('');
        setTags([]);
        setVariableDefs([]);
        setSaveError(null);
        onOpenChange(false);
      }, 1200);
    } catch (err) {
      console.error('FUQ save failed:', err);
      setSaving(false);
      setSaveError(err.message || 'Failed to save. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="sentinel-savefuqdialog-dialog" className="bg-slate-900 border-slate-700 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Bookmark className="w-4 h-4 text-amber-400" />
            Save as FUQ
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 pt-2">
          <div>
            <label className="text-xs text-slate-400 mb-1 block">Name *</label>
            <Input data-testid="sentinel-savefuqdialog-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Weekly Revenue by Region"
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Description</label>
            <Textarea data-testid="sentinel-savefuqdialog-textarea"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this query return?"
              className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 min-h-[60px] resize-none"
            />
          </div>

          <div>
            <label className="text-xs text-slate-400 mb-1 block">Tags</label>
            <div className="flex gap-2">
              <Input data-testid="sentinel-savefuqdialog-input-1"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
                placeholder="Add tag and press Enter"
                className="bg-white border-slate-300 text-slate-900 placeholder:text-slate-500"
              />
            </div>
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tags.map(tag => (
                  <Badge key={tag} className="bg-amber-100 text-amber-800 border-amber-300 text-xs flex items-center gap-1">
                    {tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-amber-950"><X className="w-3 h-3" /></button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">SQL Query</div>
            <pre className="text-xs font-mono text-cyan-300 whitespace-pre-wrap max-h-[100px] overflow-auto">{sqlQuery}</pre>
          </div>

          {queryHasVars && (
            <VariableDefinitionEditor
              sqlTemplate={sqlQuery}
              definitions={variableDefs}
              onChange={setVariableDefs}
            />
          )}

          {saveError && (
            <div className="p-2.5 rounded-lg bg-red-950/30 border border-red-500/20">
              <p className="text-xs text-red-300">{saveError}</p>
            </div>
          )}

          {!sqlQuery && (
            <div className="p-2.5 rounded-lg bg-amber-950/30 border border-amber-500/20">
              <p className="text-xs text-amber-300">No SQL query available to save. Try generating a query first.</p>
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={!name.trim() || !sqlQuery || saving || saved}
            className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {saved ? '✓ Saved!' : saving ? 'Saving...' : 'Save FUQ'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}